/**
 * A titled block of image tiles, two per row, each linking to another resource page. Desktop puts
 * the title and description on the left and the tiles on the right; mobile stacks them.
 *
 * Used by `src/pages/metal-resources/references/index.tsx` and
 * `src/pages/metal-resources/tools-calculators/index.tsx` (the "Metal Resources" cross-links, copy
 * in `src/content/ryerson/metal-resources/tools-calculators/index.ts`), and by
 * `src/components/MetalResources/MetalResourcesLandingSTS.tsx` (the "Other References" PDFs).
 */
import React from "react";
import { ContentSection } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { useTheme } from "@ryerson/frontend.theme";
import { Media } from "@ryerson/frontend.next";
import { Link } from "@ryerson/frontend.navigation";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

/** One tile: an image with its title over it, linking to `actionUrl`. */
export type MetalResourcesCard = {
	/** Site-relative image path, served from `application/public/`. */
	imageUrl: string;
	/** Shown over the image, and used as its `alt`. */
	title: string;
	/** A site path, or a full URL such as a PDF on `NEXT_PUBLIC_URI_STATIC`. */
	actionUrl: string;
};

/** One language's copy for the block. */
export type MetalResourcesContent = {
	title: string;
	description: string;
	cards: MetalResourcesCard[];
};

/** Props for {@link MetalResources}. */
export type MetalResourcesProps = {
	content: MetalResourcesContent;
};

const Left = styled.div`
	display: inline-block;
	width: 49%;
	vertical-align: top;
`;

const Right = styled.div`
	display: inline-block;
	width: 51%;
	vertical-align: top;
`;

const Contain = styled.div`
	width: 100%;
	display: block;
	height: auto;
	text-align: center;
`;

const Image = styled.div`
	display: inline-block;
	width: calc(50% - 20px);
	vertical-align: top;
	margin-bottom: 40px;
	img {
		width: 100%;
		display: block;
		height: auto;
	}
	position: relative;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				margin-bottom: 20px;
				max-width: 260px;
				width: calc(50% - 10px);
				height: 180px;
				overflow: hidden;
				text-align: center;
				img {
					width: auto;
					height: 100%;
					display: inline-block;
				}
			}
		`;
	}}
`;

const Text = styled.div`
	position: absolute;
	left: 23px;
	bottom: 20px;
	text-align: left;
	width: calc(100% - 46px);
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				left: 20px;
				bottom: 17px;
				width: calc(100% - 40px);
			}
		`;
	}}
`;

/** Renders the tile block for one language. */
const MetalResources: React.FunctionComponent<MetalResourcesProps> = ({ content }) => {
	const { theme } = useTheme();
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="primary" vPadding="145px">
					<Left>
						<Typography
							variant="h2"
							css={css`
								margin-top: 0px;
								letter-spacing: -2px;
								margin-bottom: 36px;
							`}
							type="primary"
						>
							{content.title}
						</Typography>
						<Typography
							css={css`
								max-width: 376px;
								display: block;
							`}
							type="primary"
							size="md"
						>
							{content.description}
						</Typography>
					</Left>
					<Right>
						{content.cards.map((card: MetalResourcesCard, index: number) => {
							return (
								<Link key={index} to={card.actionUrl}>
									<Image
										theme={theme}
										css={css`
											margin-right: ${index % 2 === 0 ? "40px" : "0px"};
										`}
									>
										<img src={card.imageUrl} alt={card.title} />
										<Text theme={theme}>
											<Typography
												size="md"
												color={theme.colors.primary.white}
												css={css`
													text-shadow: 0px 0px 5px
														${theme.colors.primary.darkerGray};
												`}
											>
												{card.title}
											</Typography>
										</Text>
									</Image>
								</Link>
							);
						})}
					</Right>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="primary" vPadding="30px">
					<Typography
						type="primary"
						variant="h2"
						css={css`
							letter-spacing: -1px;
							margin-top: 15px;
							margin-bottom: 20px;
						`}
					>
						{content.title}
					</Typography>
					<Typography
						type="primary"
						css={css`
							display: block;
							margin-bottom: 30px;
						`}
						size="md"
					>
						{content.description}
					</Typography>
					<Contain>
						{content.cards.map((card: MetalResourcesCard, index: number) => {
							return (
								<Link key={index} to={card.actionUrl}>
									<Image
										theme={theme}
										css={css`
											margin-right: ${index % 2 === 0 ? "20px" : "0px"};
										`}
									>
										<img src={card.imageUrl} alt={card.title} />
										<Text theme={theme}>
											<Typography
												size="md"
												color={theme.colors.primary.white}
												css={css`
													text-shadow: 0px 0px 5px
														${theme.colors.primary.darkerGray};
												`}
											>
												{card.title}
											</Typography>
										</Text>
									</Image>
									{index % 2 !== 0 ? <br /> : <></>}
								</Link>
							);
						})}
					</Contain>
				</ContentSection>
			</Media>
		</>
	);
};

export default MetalResources;
